import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import EventSwiper from '../components/EventSwiper';
import {
  PlusIcon,
  UserCircleIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DiningEvents = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [showSwiper, setShowSwiper] = useState(false);

  // Check if user has completed profile
  const hasProfile = user?.photo && user?.profession && user?.interests?.length > 0;

  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
      checkUserJoinedEvents();
      fetchMyEvents();
    }
  }, [isAuthenticated]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dining-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const availableEvents = response.data.events.filter(
        event => !event.participants?.some(
          p => p.user?._id?.toString() === user?._id?.toString()
        )
      );

      setEvents(availableEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkUserJoinedEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dining-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const joined = response.data.events
        .filter(event => event.participants?.some(
          p => p.user?._id?.toString() === user?._id?.toString()
        ))
        .map(event => event.id);

      setJoinedEvents(joined);
    } catch (err) {
      console.error('Error checking joined events:', err);
    }
  };

  const fetchMyEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dining-events/my-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyEvents(response.data.events || []);
    } catch (err) {
      console.error('Error fetching my events:', err);
    }
  };

  const getEventStatus = (eventDate) => {
    const now = new Date();
    const date = new Date(eventDate);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const eventDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (eventDay.getTime() === today.getTime()) return 'Today';
    if (eventDay > today) return 'Upcoming';
    return 'Completed';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleJoin = async (event) => {
    if (!hasProfile) {
      setShowProfilePrompt(true);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/dining-events/${event.id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate(`/dining-events/${event.id}`);
    } catch (err) {
      const reason = err.response?.data?.message || 'Unknown error';
      setError(`Unable to join the event: ${reason}`);
    }
  };

  const handleSkip = (event) => {
    console.log('Skipped event:', event.id);
  };

  const handleCreateEvent = () => {
    if (!hasProfile) {
      setShowProfilePrompt(true);
      return;
    }
    navigate('/dining-events/create');
  };

  const handleExploreEvents = () => {
    if (!hasProfile) {
      setShowProfilePrompt(true);
      return;
    }
    setShowSwiper(true);
  };

  // Profile Setup Prompt
  if (showProfilePrompt) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-primary-500 to-rose-500 rounded-2xl flex items-center justify-center mb-6">
            <UserGroupIcon className="h-12 w-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Complete Your Profile
          </h2>
          <p className="text-gray-600 mb-8">
            Add a photo, your profession, and interests so others can get to know you before meeting up.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/hola-profile-setup')}
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-xl font-semibold text-lg hover:bg-gray-800 transition"
            >
              Complete Profile
              <ArrowRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowProfilePrompt(false)}
              className="w-full text-gray-500 hover:text-gray-700 font-medium"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Event Swiper View
  if (showSwiper) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <div className="bg-white px-4 py-4 shadow-sm sticky top-0 z-10">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <button
              onClick={() => setShowSwiper(false)}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
            <h1 className="text-lg font-bold text-gray-900">Find Events</h1>
            <button
              onClick={() => navigate('/hola-profile-setup')}
            >
              {user?.photo ? (
                <img
                  src={user.photo}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover border-2 border-primary-500"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserCircleIcon className="h-6 w-6 text-gray-400" />
                </div>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="px-4 py-2">
            <div className="max-w-md mx-auto p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-center text-sm">
              {error}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          {events.length > 0 ? (
            <EventSwiper
              events={events}
              onJoin={handleJoin}
              onSkip={handleSkip}
              userJoinedEvents={joinedEvents}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <CalendarDaysIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Events Yet</h3>
              <p className="text-gray-600 mb-6">
                Be the first to create an event and invite neighbors to join!
              </p>
              <button
                onClick={handleCreateEvent}
                className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition"
              >
                <PlusIcon className="h-5 w-5" />
                Create Event
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleCreateEvent}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gray-900 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-800 hover:shadow-xl transition-all z-50"
        >
          <PlusIcon className="h-8 w-8" />
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Landing Page with Video Background
  return (
    <div className="min-h-screen bg-gray-900 relative overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 flex items-center justify-center bg-gray-900">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-contain opacity-90"
        >
          <source src="/connect-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/30 via-transparent to-gray-900/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Hero Section */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            Meet Your Neighbors<br />
            <span className="text-primary-400">Over Great Food</span>
          </h1>

          <p className="text-lg text-white/80 max-w-md mb-8">
            Join community dining events at local restaurants.
            Make new friends, share stories, and discover your neighborhood.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
            <button
              onClick={handleExploreEvents}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-white text-gray-900 rounded-xl font-semibold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              <CalendarDaysIcon className="h-6 w-6" />
              Find Events
            </button>
            <button
              onClick={handleCreateEvent}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 transition shadow-lg"
            >
              <PlusIcon className="h-6 w-6" />
              Host Event
            </button>
          </div>
        </div>

        {/* My Events */}
        {myEvents.length > 0 && (
          <div className="px-6 pb-8">
            <div className="max-w-md mx-auto">
              {/* Hosting */}
              {myEvents.filter(e => e.organizer?._id === user?._id).length > 0 && (
                <>
                  <h3 className="text-white font-bold text-lg mb-4">Hosting</h3>
                  <div className="space-y-3 mb-6">
                    {myEvents.filter(e => e.organizer?._id === user?._id).map((event) => {
                      const status = getEventStatus(event.eventDate);
                      return (
                        <div
                          key={event.id}
                          onClick={() => navigate(`/dining-events/${event.id}`)}
                          className="bg-white/10 backdrop-blur-md rounded-xl p-4 cursor-pointer hover:bg-white/20 transition border-l-4 border-primary-400"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-white font-semibold truncate flex-1">
                              {event.restaurant?.name || 'Restaurant'}
                            </p>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ml-2 ${
                              status === 'Today' ? 'bg-green-500/20 text-green-300' :
                              status === 'Upcoming' ? 'bg-primary-500/20 text-primary-300' :
                              'bg-white/10 text-white/50'
                            }`}>
                              {status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-white/60 text-sm">
                            <div className="flex items-center gap-1">
                              <CalendarDaysIcon className="h-4 w-4" />
                              <span>{formatDate(event.eventDate)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ClockIcon className="h-4 w-4" />
                              <span>{event.eventTime}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <UserGroupIcon className="h-4 w-4" />
                              <span>{event.participantsCount || event.participants?.length || 0}/{event.totalSeats}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Joining */}
              {myEvents.filter(e => e.organizer?._id !== user?._id).length > 0 && (
                <>
                  <h3 className="text-white font-bold text-lg mb-4">Joined</h3>
                  <div className="space-y-3">
                    {myEvents.filter(e => e.organizer?._id !== user?._id).map((event) => {
                      const status = getEventStatus(event.eventDate);
                      return (
                        <div
                          key={event.id}
                          onClick={() => navigate(`/dining-events/${event.id}`)}
                          className="bg-white/10 backdrop-blur-md rounded-xl p-4 cursor-pointer hover:bg-white/20 transition"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-white font-semibold truncate flex-1">
                              {event.restaurant?.name || 'Restaurant'}
                            </p>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ml-2 ${
                              status === 'Today' ? 'bg-green-500/20 text-green-300' :
                              status === 'Upcoming' ? 'bg-primary-500/20 text-primary-300' :
                              'bg-white/10 text-white/50'
                            }`}>
                              {status}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-white/60 text-sm">
                            <div className="flex items-center gap-1">
                              <CalendarDaysIcon className="h-4 w-4" />
                              <span>{formatDate(event.eventDate)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ClockIcon className="h-4 w-4" />
                              <span>{event.eventTime}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <UserGroupIcon className="h-4 w-4" />
                              <span>{event.participantsCount || event.participants?.length || 0}/{event.totalSeats}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="px-6 pb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 max-w-md mx-auto">
            <h3 className="text-white font-bold text-lg mb-4 text-center">How It Works</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <CalendarDaysIcon className="h-5 w-5 text-primary-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Join or Host</p>
                  <p className="text-white/60 text-sm">Browse upcoming events or create your own at a favorite spot</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 text-primary-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Meet New People</p>
                  <p className="text-white/60 text-sm">Connect with neighbors who share your interests and love for food</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Build Community</p>
                  <p className="text-white/60 text-sm">Turn strangers into friends over shared meals and conversations</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Events Preview */}
        {events.length > 0 && (
          <div className="px-6 pb-8">
            <div className="max-w-md mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg">Upcoming Events</h3>
                <button
                  onClick={handleExploreEvents}
                  className="text-primary-400 text-sm font-medium"
                >
                  See All →
                </button>
              </div>

              <div className="space-y-3">
                {events.slice(0, 2).map((event) => (
                  <div
                    key={event.id}
                    onClick={() => handleExploreEvents()}
                    className="bg-white/10 backdrop-blur-md rounded-xl p-4 cursor-pointer hover:bg-white/20 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="h-6 w-6 text-primary-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{event.title}</p>
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                          <MapPinIcon className="h-4 w-4" />
                          <span className="truncate">{event.restaurant?.name || 'Local Restaurant'}</span>
                        </div>
                      </div>
                      <div className="text-white/40">
                        <ArrowRightIcon className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Profile Completion Prompt */}
        {!hasProfile && (
          <div className="px-6 pb-6">
            <div
              onClick={() => navigate('/hola-profile-setup')}
              className="max-w-md mx-auto p-4 bg-gradient-to-r from-primary-600 to-rose-600 rounded-xl cursor-pointer hover:shadow-lg transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">Complete Your Profile</p>
                  <p className="text-white/80 text-sm">Add photo & interests to join events</p>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiningEvents;
